import { fetchAuthSession } from "@aws-amplify/auth";

type Tokens = { accessToken: string | null; idToken: string | null };

// Refresh this long before the token's `exp` so a request sent right at the
// boundary never carries a token the server will reject.
const EXPIRY_MARGIN_MS = 60_000;

/** `exp` of a JWT in ms, or null when it can't be read. */
function tokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = JSON.parse(json).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

// One cache of the Cognito tokens for the whole app. Every request reads from
// here (the Axios interceptor directly, the rest through
// `getAPIRequestTokenHeaders`), so this is where freshness has to live: a
// token is refreshed shortly *before* it expires, not after a request has
// already failed with it. Before this the cache was never invalidated, so the
// first request after each hourly expiry went out with a dead token.
class TokenService {
  static accessToken: string | null = null;
  static idToken: string | null = null;
  private static inflight: Promise<Tokens> | null = null;

  /** True when there is no token, or it expires within the safety margin. */
  static isStale(): boolean {
    const exp = tokenExpiryMs(this.accessToken);
    return exp === null || exp - Date.now() < EXPIRY_MARGIN_MS;
  }

  /** Ms until the cached token should be refreshed (0 when already due). */
  static msUntilRefresh(): number {
    const exp = tokenExpiryMs(this.accessToken);
    if (exp === null) return 0;
    return Math.max(0, exp - EXPIRY_MARGIN_MS - Date.now());
  }

  static async getTokens(): Promise<Tokens> {
    if (!this.isStale()) {
      return { accessToken: this.accessToken, idToken: this.idToken };
    }
    return this.refreshTokens();
  }

  /**
   * Re-reads the session from Amplify, which uses the refresh token when the
   * access token has expired. `force` makes Amplify refresh even if it thinks
   * the token is still valid — used after the server has rejected it, where
   * trusting the client clock would just replay the same dead token.
   *
   * Concurrent callers share one refresh rather than each hitting Cognito.
   */
  static refreshTokens(force = false): Promise<Tokens> {
    if (this.inflight) return this.inflight;

    this.inflight = (async () => {
      try {
        const session = await fetchAuthSession({ forceRefresh: force });
        this.accessToken = session.tokens?.accessToken?.toString() || null;
        this.idToken = session.tokens?.idToken?.toString() || null;
        return { accessToken: this.accessToken, idToken: this.idToken };
      } finally {
        this.inflight = null;
      }
    })();

    return this.inflight;
  }

  static clear() {
    this.accessToken = null;
    this.idToken = null;
  }
}

export default TokenService;
